import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function EbookSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/ebook/status/${sessionId}`);
        if (data.payment_status === "paid" && data.download_url) {
          setState({ status: "paid", data });
          return;
        }
        if (attempts >= 10) { setState({ status: "pending", data }); return; }
      } catch {
        if (attempts >= 5) { setState({ status: "error", data: null }); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <section className="bg-[#F5F4EF] min-h-[70vh] flex items-center py-20">
      <div className="container-if max-w-xl mx-auto text-center">
        {state.status === "checking" && (
          <div data-testid="ebook-success-checking">
            <Clock className="w-10 h-10 text-[#4F5D1E] mx-auto mb-4 animate-pulse" />
            <h1 className="font-display text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-[#5B5F50] text-sm" style={{ fontFamily: "Inter, sans-serif" }}>This usually takes a few seconds.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div data-testid="ebook-success-paid">
            <div className="w-14 h-14 rounded-full bg-[#4F5D1E]/15 border border-[#4F5D1E]/40 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-[#4F5D1E]" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl mb-3">You're approved.</h1>
            <p className="text-[#5B5F50] text-sm mb-8" style={{ fontFamily: "Inter, sans-serif" }}>
              Your personal copy of <strong>From Denied to Approved</strong> is ready.
              We also emailed the link to <strong>{state.data.email}</strong>.
            </p>
            <a href={`${API_BASE}${state.data.download_url}`} className="btn-gold inline-flex" data-testid="ebook-download-btn">
              <Download className="w-4 h-4" /> Download Your Ebook
            </a>
            <p className="text-[11px] text-[#5B5F50] mt-6" style={{ fontFamily: "Inter, sans-serif" }}>
              Your link allows {state.data.downloads_left ?? 2} downloads and expires in 7 days.
              Each page is licensed to your email. Need a reset later? Reply to your purchase email.
            </p>
          </div>
        )}
        {state.status === "pending" && (
          <div data-testid="ebook-success-pending">
            <Clock className="w-10 h-10 text-amber-600 mx-auto mb-4" />
            <h1 className="font-display text-3xl mb-2">Payment still processing</h1>
            <p className="text-[#5B5F50] text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
              No worries — as soon as it clears, your download link will arrive by email automatically.
            </p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="ebook-success-error">
            <h1 className="font-display text-3xl mb-2">Something's off.</h1>
            <p className="text-[#5B5F50] text-sm mb-6" style={{ fontFamily: "Inter, sans-serif" }}>We couldn't confirm this purchase. If you were charged, your link will still arrive by email.</p>
            <Link to="/ebook" className="btn-outline">Back to the ebook</Link>
          </div>
        )}
      </div>
    </section>
  );
}
